import { NgClass, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  TemplateRef,
  Type,
} from '@angular/core';
import { ModalService } from '@shared/components/modal/modal.service';
import { SchemeColorVariant } from '@shared/interfaces/scheme-color-variant';
import { Observable, Subject } from 'rxjs';
import { SpinnerComponent } from '../spinner/spinner.component';
export interface ModalOptions {
  id?: string;
  size?: ModalSize;
  title?: string;
  description?: string;
  icon?: string;
  confirmationLabel?: string;
  confirmationDisabled?: boolean;
  cancellationLabel?: string;
  hasConfirmation?: boolean;
  hasCancellation?: boolean;
  closeOnCancellation?: boolean;
  closeOnConfirmation?: boolean;
  cancellationLoading?: boolean;
  confirmationLoading?: boolean;
  isOpen?: boolean;
  animationDelay?: number;
  enableScroll?: boolean;
  style?: 'modal' | 'alert';
  variant?: SchemeColorVariant;
  componentInputs?: Record<string, unknown>;
}

export interface ModalResult {
  event: ModalEventType;
  modal: Modal;
}

export enum ModalEventType {
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  CONFIRMED = 'CONFIRMED',
}

export enum ModalSize {
  EXTRA_SMALL = 'xs',
  SMALL = 'sm',
  MEDIUM = 'md',
  LARGE = 'lg',
  EXTRA_LARGE = 'xl',
}

export interface Modal {
  content?: TemplateRef<Element> | Type<unknown>;
  options: ModalOptions;
  result$: Subject<ModalResult>;
}

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  imports: [NgClass, NgTemplateOutlet, NgComponentOutlet, SpinnerComponent],
  styleUrls: ['../color-scheme.scss'],
})
export class ModalComponent implements AfterViewInit {
  baseZIndex = 51;
  modalEventType = ModalEventType;

  modals: Modal[] = [];

  modalService = inject(ModalService);
  changeDetectorRef = inject(ChangeDetectorRef);

  ngAfterViewInit(): void {
    this.modalService.register(this);
  }

  open(
    content?: Modal['content'],
    options: ModalOptions = {}
  ): Observable<ModalResult> {
    const result$ = new Subject<ModalResult>();
    this.modals.push({ content, options, result$ });

    const delay = options.animationDelay;

    setTimeout(() => {
      const modal = this.modals[this.modals.length - 1];
      modal.options.isOpen = true;
      this.changeDetectorRef.detectChanges();

      document.body.classList.add('overflow-hidden');
    }, delay);

    return result$.asObservable();
  }

  close(modal: Modal, event = ModalEventType.CLOSED) {
    const delay = modal.options.animationDelay;
    modal.options.isOpen = false;

    setTimeout(() => {
      const index = this.modals.indexOf(modal);
      if (index >= 0) {
        this.modals.splice(index, 1);
        modal.result$.next({ event, modal });
        modal.result$.complete();
        this.changeDetectorRef.detectChanges();

        if (this.modals.length === 0) {
          document.body.classList.remove('overflow-hidden');
        }
      }
    }, delay);
  }

  closeAll() {
    while (this.modals.length > 0) {
      const modal = this.modals.pop()!;
      modal.result$.next({ event: ModalEventType.CLOSED, modal });
      modal.result$.complete();
    }
  }

  handleModalAction(modal: Modal, event: ModalEventType) {
    if (
      (event === ModalEventType.CANCELLED &&
        !modal.options.closeOnCancellation) ||
      (event === ModalEventType.CONFIRMED && !modal.options.closeOnConfirmation)
    ) {
      modal.result$.next({ event, modal });
      return;
    }
    this.close(modal, event);
  }

  getModalSize(size: ModalOptions['size']): string {
    const sizeClass = {
      xs: 'w-[32rem]',
      sm: 'w-[42rem]',
      md: 'w-[52rem]',
      lg: 'w-[62rem]',
      xl: 'w-[72rem]',
    };
    return size ? sizeClass[size] : sizeClass.md;
  }

  isTemplate(content: Modal['content']): content is TemplateRef<Element> {
    return content instanceof TemplateRef;
  }
}
